import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { showSuccessToast } from "../../utils/toastHelper";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import Box from "@material-ui/core/Box";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ViewListIcon from "@material-ui/icons/ViewList";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ViewAgendaIcon from "@material-ui/icons/ViewAgenda";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import TodayIcon from "@material-ui/icons/Today";
import PageBase from "../../components/PageBase";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
// import MessageModal from "../../components/MessageModal"
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment-timezone";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import "moment/locale/pt-br";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FullCalendarView from "./FullCalendarView";
import "./Schedules.css";
import { resolveCompanyTimezone, getStoredCompanyTimezone } from "../../utils/companyTimezone";

// ✅ REMOVIDO: Não configurar timezone padrão do moment
// O timezone será detectado dinamicamente do usuário/navegador

// Defina a função getUrlParam antes de usá-la
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    minHeight: "calc(100vh - 200px)",
  },
  calendarWrapper: {
    // Estilos para FullCalendar são aplicados via CSS externo
  },
}));

// ✅ PASSO 1: Função para obter timezone da empresa (não do navegador)
// Usa a mesma prioridade canônica do projeto:
// 1º user.company.timezone (AuthContext → API)
// 2º localStorage companyTimezone (gravado no login)
// 3º fallback: America/Sao_Paulo
const getCompanyTimeZoneForUser = (user) => {
  const tz = resolveCompanyTimezone(
    user?.company?.timezone || getStoredCompanyTimezone()
  );
  console.log("[SCHEDULES][TZ] timezone da empresa:", {
    sourceCompany: user?.company?.timezone,
    sourceStorage: getStoredCompanyTimezone(),
    resolved: tz,
  });
  return tz;
};

const Schedules = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [currentView, setCurrentView] = useState("month");
  // Inicializar com data atual (será ajustada no FullCalendarView baseado na view)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarApi, setCalendarApi] = useState(null);
  const [calendarTitle, setCalendarTitle] = useState("");

  // ✅ PASSO 1: Resolver timezone da empresa (não do navegador)
  const userTimeZone = useMemo(() => getCompanyTimeZoneForUser(user), [user]);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error(i18n.t("schedules.toasts.noPermissionRedirect"));
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    // handleOpenScheduleModalFromContactId();
    // const socket = socketManager.GetSocket(user.companyId, user.id);

    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);

    return () => {
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
    };
  }, [socket]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = useCallback((scheduleId) => {
    // ✅ Parte C: Revalidar canEdit antes de abrir
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;
    
    const canEdit = schedule.status !== "ENVIADA" && !schedule.sentAt;
    if (!canEdit) {
      toast.warning(i18n.t("schedules.toasts.sentEditBlocked"));
      return;
    }
    
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  }, [schedules]);

  const handleEditScheduleFromEvent = useCallback((scheduleId) => {
    handleEditSchedule(scheduleId);
  }, [handleEditSchedule]);

  const handleDeleteSchedule = useCallback(async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      showSuccessToast(i18n.t("schedules.toasts.deleted"));
      
      // Remover do estado imediatamente
      dispatch({ type: "DELETE_SCHEDULE", payload: scheduleId });
      
      setDeletingSchedule(null);
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const handleDeleteScheduleFromEvent = useCallback(async (scheduleId) => {
    // ✅ Parte C: Revalidar canEdit antes de excluir
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;
    
    const canEdit = schedule.status !== "ENVIADA" && !schedule.sentAt;
    if (!canEdit) {
      toast.error(i18n.t("schedules.toasts.sentDeleteBlocked"));
      return;
    }

    const ok = window.confirm(i18n.t("schedules.confirmations.deleteSchedule"));
    if (!ok) return;

    await handleDeleteSchedule(scheduleId);
  }, [schedules, handleDeleteSchedule]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      // Quando mudar de view, mostrar a data atual baseada na view
      const today = new Date();
      let dateToShow = today;
      
      // Ajustar data baseado na view
      if (newView === "week" || newView === "agenda") {
        // Para semana: mostrar início da semana atual (segunda-feira)
        // moment().startOf('week') usa domingo por padrão, então precisamos ajustar
        // Como configuramos firstDay={1} (segunda), precisamos usar isoWeek
        dateToShow = moment(today).startOf('isoWeek').toDate();
      } else if (newView === "day") {
        // Para dia: mostrar dia atual
        dateToShow = today;
      } else {
        // Para mês: mostrar mês atual
        dateToShow = today;
      }
      
      setCurrentView(newView);
      setCurrentDate(dateToShow);
      
      // Sincronizar com FullCalendar - o FullCalendarView.jsx já cuida disso via useEffect
      // Apenas atualizar o título se a API estiver disponível
      if (calendarApi) {
        // Pequeno delay para garantir que o FullCalendarView processou a mudança
        setTimeout(() => {
          if (calendarApi.view) {
            setCalendarTitle(calendarApi.view.title);
          }
        }, 100);
      }
    }
  };

  // Navegação do calendário
  const handleNavigateToday = () => {
    const today = new Date();
    setCurrentDate(today);
    if (calendarApi) {
      calendarApi.today();
      setCalendarTitle(calendarApi.view.title);
    }
  };

  const handleNavigatePrevious = () => {
    if (calendarApi) {
      calendarApi.prev();
      setCurrentDate(calendarApi.getDate());
      setCalendarTitle(calendarApi.view.title);
    }
  };

  const handleNavigateNext = () => {
    if (calendarApi) {
      calendarApi.next();
      setCurrentDate(calendarApi.getDate());
      setCalendarTitle(calendarApi.view.title);
    }
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
    // Sincronizar com FullCalendar
    if (calendarApi) {
      calendarApi.gotoDate(date);
    }
  };

  // Handlers para FullCalendar
  const handleFullCalendarViewChange = useCallback((view) => {
    setCurrentView(view);
    // Atualizar título quando view mudar
    if (calendarApi) {
      setCalendarTitle(calendarApi.view.title);
    }
  }, [calendarApi]);

  const handleFullCalendarDateChange = useCallback((date) => {
    setCurrentDate(date);
    // Atualizar título quando data mudar
    if (calendarApi) {
      setCalendarTitle(calendarApi.view.title);
    }
  }, [calendarApi]);

  const handleFullCalendarEventClick = useCallback((eventInfo) => {
    // ✅ Parte C: Ao clicar no evento, abrir modal (editável ou readonly)
    const { scheduleId, canEdit } = eventInfo.extendedProps;
    if (scheduleId) {
      const schedule = schedules.find(s => s.id === scheduleId);
      if (schedule) {
        setSelectedSchedule(schedule);
        setScheduleModalOpen(true);
      }
    }
  }, [schedules]);

  const handleGetCalendarApi = useCallback((api) => {
    setCalendarApi(api);
    // Inicializar título
    if (api) {
      setCalendarTitle(api.view.title);
    }
  }, []);

  // Formatar título do calendário baseado na view atual
  const getCalendarTitle = () => {
    if (calendarTitle) {
      // Usar título do FullCalendar (atualizado via state)
      return calendarTitle;
    }
    
    // Fallback caso o título ainda não esteja disponível
    return moment(currentDate).format("MMMM [de] YYYY");
  };

  // ✅ PASSO 3: Mapeamento correto de eventos com timezone do usuário
  // REGRA: sendAt vem em UTC (ISO com Z), exemplo: 2026-02-03T10:25:00.000Z (UTC) = 07:25 em SP
  // Parsing correto: usar apenas new Date(sendAt) - sem conversões manuais
  // FullCalendar com timeZone={userTimeZone} faz a conversão automaticamente
  const fullCalendarEvents = useMemo(() => {
    return schedules.map((schedule, index) => {
      let startDate;
      let endDate;
      
      try {
        const rawSendAt = schedule.sendAt;
        
        // ✅ PASSO 3: Parsing correto - apenas new Date() quando vem com Z
        if (typeof rawSendAt === 'string' && rawSendAt.includes('Z')) {
          // ISO UTC: usar new Date() diretamente - sem conversões
          // Isso representa o instante correto; FullCalendar renderiza no timeZone configurado
          startDate = new Date(rawSendAt);
          
          // Log para debug (apenas primeiro evento)
          if (index === 0) {
            console.log("[SCHEDULES] Evento de exemplo (primeiro):", {
              rawSendAt,
              startToString: startDate.toString(),
              startToISOString: startDate.toISOString(),
              startGetHours: startDate.getHours(),
              startGetUTCHours: startDate.getUTCHours(),
              userTimeZone,
            });
          }
        } else if (rawSendAt instanceof Date) {
          // Date object: usar diretamente
          startDate = rawSendAt;
          
          // Log para debug (apenas primeiro evento)
          if (index === 0) {
            console.log("[SCHEDULES] Evento de exemplo (primeiro, Date object):", {
              rawSendAt: rawSendAt.toString(),
              startToString: startDate.toString(),
              startToISOString: startDate.toISOString(),
              startGetHours: startDate.getHours(),
              startGetUTCHours: startDate.getUTCHours(),
              userTimeZone,
            });
          }
        } else {
          // Fallback: tentar criar Date
          startDate = new Date(rawSendAt);
          if (isNaN(startDate.getTime())) {
            throw new Error(`Data inválida: ${rawSendAt}`);
          }
          
          // Log para debug (apenas primeiro evento)
          if (index === 0) {
            console.log("[SCHEDULES] Evento de exemplo (primeiro, fallback):", {
              rawSendAt,
              startToString: startDate.toString(),
              startToISOString: startDate.toISOString(),
              startGetHours: startDate.getHours(),
              startGetUTCHours: startDate.getUTCHours(),
              userTimeZone,
            });
          }
        }
        
        // ✅ PASSO 3: End apenas para exibição (30 minutos)
        // Criar end simplesmente: start + 30 minutos
        endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
      } catch (error) {
        console.error(`[FULLCALENDAR] Erro ao processar sendAt do agendamento ID ${schedule.id}:`, schedule.sendAt, error);
        // Fallback: usar data atual
        startDate = new Date();
        endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
      }
      
      // ✅ Parte A: Garantir que extendedProps tem status/sentAt/canEdit
      const canEdit = schedule.status !== "ENVIADA" && !schedule.sentAt;
      
      // ✅ Log temporário (apenas primeiro evento)
      if (index === 0) {
        console.log("[SCHEDULES] Evento de exemplo - extendedProps:", {
          scheduleId: schedule.id,
          status: schedule.status,
          sentAt: schedule.sentAt,
          canEdit: canEdit,
        });
      }
      
      return {
        id: String(schedule.id),
        title:
          schedule?.contact?.name ||
          schedule?.contactName ||
          schedule?.contact?.number ||
          "Sem nome",
        start: startDate,
        end: endDate,
        extendedProps: {
          scheduleId: schedule.id,
          status: schedule.status,
          sentAt: schedule.sentAt,
          canEdit: canEdit,
          schedule: schedule, // Manter para compatibilidade
          contactName:
            schedule?.contact?.name ||
            schedule?.contactName ||
            schedule?.contact?.number,
          body: schedule.body,
        },
        backgroundColor: "#667eea",
        borderColor: "#764ba2",
        textColor: "#ffffff",
      };
    });
  }, [schedules, userTimeZone]);


  return (
    <PageBase
      className="schedules-page app-page-root"
      title={`${i18n.t("schedules.title")} (${schedules.length})`}
      actions={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </div>
      }
      toolbar={
        <div
          className="schedules-toolbar"
          style={{
            padding: theme.spacing(1, 2),
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
            borderRadius: "8px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            {/* Navegação e Título */}
            <Box display="flex" alignItems="center" style={{ gap: theme.spacing(1) }}>
              <Button
                size="small"
                onClick={handleNavigatePrevious}
                style={{
                  minWidth: 40,
                  padding: "6px 8px",
                }}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                size="small"
                onClick={handleNavigateToday}
                style={{
                  minWidth: 40,
                  padding: "6px 8px",
                }}
              >
                <TodayIcon />
              </Button>
              <Button
                size="small"
                onClick={handleNavigateNext}
                style={{
                  minWidth: 40,
                  padding: "6px 8px",
                }}
              >
                <ArrowForwardIcon />
              </Button>
              <Box
                component="span"
                style={{
                  marginLeft: theme.spacing(2),
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  textTransform: "capitalize",
                }}
              >
                {getCalendarTitle()}
              </Box>
            </Box>

            {/* Botões de View */}
            <ToggleButtonGroup
              value={currentView}
              exclusive
              onChange={handleViewChange}
              aria-label={i18n.t("schedules.aria.calendarView")}
              size="small"
              style={{
                backgroundColor: theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.05)" 
                  : "rgba(0, 0, 0, 0.02)",
              }}
            >
              <ToggleButton 
                value="month" 
                aria-label={i18n.t("schedules.aria.monthView")}
                style={{
                  minWidth: 100,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                <ViewModuleIcon style={{ marginRight: 6, fontSize: 18 }} />
                {i18n.t("schedules.month")}
              </ToggleButton>
              <ToggleButton 
                value="week" 
                aria-label={i18n.t("schedules.aria.weekView")}
                style={{
                  minWidth: 100,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                <ViewListIcon style={{ marginRight: 6, fontSize: 18 }} />
                {i18n.t("schedules.week")}
              </ToggleButton>
              <ToggleButton 
                value="day" 
                aria-label={i18n.t("schedules.aria.dayView")}
                style={{
                  minWidth: 100,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                <CalendarTodayIcon style={{ marginRight: 6, fontSize: 18 }} />
                {i18n.t("schedules.day")}
              </ToggleButton>
              <ToggleButton 
                value="agenda" 
                aria-label={i18n.t("schedules.aria.agendaView")}
                style={{
                  minWidth: 100,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                <ViewAgendaIcon style={{ marginRight: 6, fontSize: 18 }} />
                {i18n.t("schedules.agenda")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </div>
      }
      containerProps={{
        onScroll: handleScroll,
        className: `${classes.mainPaper} schedules-page`,
      }}
    >
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          // aria-labelledby="form-dialog-title"
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
          user={user}
        />
      )}
      <div className={`${classes.calendarWrapper} schedules-calendar-outer`}>
        <FullCalendarView
          events={fullCalendarEvents}
          currentView={currentView}
          currentDate={currentDate}
          userTimeZone={userTimeZone}
          onViewChange={handleFullCalendarViewChange}
          onDateChange={handleFullCalendarDateChange}
          onEventClick={handleFullCalendarEventClick}
          onEditSchedule={handleEditScheduleFromEvent}
          onDeleteSchedule={handleDeleteScheduleFromEvent}
          getCalendarApi={handleGetCalendarApi}
        />
      </div>
    </PageBase>
  );
};

export default Schedules;
